import React, { useCallback, useMemo } from 'react'
import { withRouter, Redirect, useLocation, useHistory } from 'react-router'

import { ResetPasswordModal } from '../AuthModals/ResetPasswordModal/ResetPasswordModal'
import { ForgotPasswordModal } from '../AuthModals/ForgotPasswordModal/ForgotPasswordModal'
import { LoginModal } from '../AuthModals/LoginModal/LoginModal'
import { CreateAccountModal } from '../AuthModals/CreateAccountModal/CreateAccountModal'
import { VerifyEmail } from '../VerifyEmail/VerifyEmail'
import { Link } from '../Link/Link'
import { FulfillmentSelectorModal } from '../FulfillmentSelectorModal/FulfillmentSelectorModal'
import { MFAVerificationModal } from '../AuthModals/MFAVerificationModal/MFAVerificationModal'
import { SearchModal } from '../Search/SearchModal/SearchModal'
import { MealSelectorModal } from '../MealSelector/MealSelectorModal/MealSelectorModal'
import { useFulfillmentAvailability } from '../../hooks/use-fulfillment-availability'
import { useAvailability } from '@local/do-secundo-availability-provider'
import { useVariant } from '../../hooks/use-variant'
import { XP_MEAL_SELECTOR } from '../ExperimentsProvider/experiment-definitions'
import { MenuPerformanceModal } from '@local/do-secundo-performance-audit'

import {
  CreatePasswordlessAccountModal,
  LoginPasswordlessModal,
  PasswordlessCodeVerificationModal,
  ConnectAccountModal
} from '@local/do-secundo-auth-modals'
import {
  useIsAccountLinkingEnabled,
  useIsPasswordlessAuthEnabled,
  useIsDeleteAccountEnabled,
  DeleteAccountModalExperience
} from '@local/do-secundo-passwordless-authentication'
import { useFlag, FF } from '@local/do-secundo-feature-flag'

const paramsToCleanUp = ['resetToken', 'confirmToken']

const ModeRouter = withRouter(({ location, history }) => {
  const searchParams = new URLSearchParams(location.search)
  searchParams.delete('mode')
  paramsToCleanUp.forEach((param) => searchParams.delete(param))
  const closeLocation = {
    ...location,
    search: searchParams.toString()
  }
  const pushLocation = () => history.push(closeLocation)
  const replaceLocationClearState = () => {
    const clearState = {
      ...closeLocation,
      state: {}
    }
    history.replace(clearState)
  }
  const replaceLocation = () => history.replace(closeLocation)
  const mealSelectorVariant = !!useVariant(XP_MEAL_SELECTOR.experimentName)
  const fulfillmentAvailable = useFulfillmentAvailability()
  const { orderingAvailable } = useAvailability()
  const passwordlessAuthEnabled = useIsPasswordlessAuthEnabled()
  const accountLinkingEnabled = useIsAccountLinkingEnabled()
  const DELETE_ACCOUNT_ENABLED = useIsDeleteAccountEnabled()
  const EDIT_MODE_ENABLED = useFlag(FF.EDIT_MODE)

  const routes = {
    login: <LoginModal onClose={pushLocation} />,
    create: <CreateAccountModal onClose={pushLocation} />,
    verify: <VerifyEmail onClose={pushLocation} />,
    search: <SearchModal onClose={pushLocation} />,
    forgot: <ForgotPasswordModal onClose={pushLocation} />,
    reset: <ResetPasswordModal onClose={replaceLocation} />,
    mfa: <MFAVerificationModal onClose={pushLocation} />
  }

  if (EDIT_MODE_ENABLED) {
    routes.performance = <MenuPerformanceModal onClose={pushLocation} />
  }

  if (passwordlessAuthEnabled) {
    routes.login = <LoginPasswordlessModal onClose={pushLocation} />
    routes.create = (
      <CreatePasswordlessAccountModal onClose={replaceLocationClearState} />
    )
    routes.sms = (
      <PasswordlessCodeVerificationModal onClose={replaceLocationClearState} />
    )

    if (accountLinkingEnabled) {
      routes.connect = <ConnectAccountModal onClose={pushLocation} />
      routes.connectLogin = <LoginModal onClose={pushLocation} />
    }
    if (DELETE_ACCOUNT_ENABLED) {
      routes.delete = <DeleteAccountModalExperience onClose={pushLocation} />
    }
  }

  if (fulfillmentAvailable) {
    routes.fulfillment = <FulfillmentSelectorModal onClose={replaceLocation} />
  }
  if (mealSelectorVariant && orderingAvailable) {
    routes.shuffle = <MealSelectorModal onClose={pushLocation} />
  }
  const search = new URLSearchParams(location.search)
  const mode = search.get('mode')
  return routes[mode] || null
})

const getModeComponent =
  (Component) =>
  ({ mode, state, pathname, ...props }) => {
    const location = useLocation()
    const searchParams = new URLSearchParams(location.search)
    searchParams.set('mode', mode)
    paramsToCleanUp.forEach((param) => searchParams.delete(param))
    return (
      <Component
        {...props}
        to={{ pathname, search: searchParams.toString(), state }}
      />
    )
  }

const ModeRedirect = getModeComponent(Redirect)
const ModeLink = getModeComponent(Link)

export const useHasMode = (value) => {
  const location = useLocation()
  return new URLSearchParams(location.search).get('mode') === value
}

/**
 *
 * @param {Object} useModeHistoryPushCallbackParams
 * @param {string} useModeHistoryPushCallbackParams.mode
 * @param {import('history').LocationDescriptorObject|string} useModeHistoryPushCallbackParams.to - A location object or pathname string (note: replaces search with 'mode' parameter)
 */
export const useModeHistoryPushCallback = ({
  mode,
  to = { pathname: '/' }
}) => {
  const history = useHistory()
  const location = useLocation()
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )
  searchParams.set('mode', mode)
  paramsToCleanUp.forEach((param) => searchParams.delete(param))

  if (typeof to === 'string') {
    to = { pathname: to }
  }

  return useCallback(() => {
    history.push({
      ...to,
      search: '?' + searchParams.toString()
    })
  }, [history, searchParams, to])
}

export { ModeRouter, ModeRedirect, ModeLink }
